/**
 * Duration sanitisation and normalisation utilities.
 *
 * Replicates the duration parsing logic from dmn/js/services/bookingManager.js
 * (lines 78-81) and the normalisation rules described in the specification.
 */

/**
 * Sanitise a raw duration string by stripping non-numeric characters
 * (except `.` and `-`).
 *
 * Mirrors: `('' + params.duration).replace(/[^0-9.-]/g, '')`
 * from dmn bookingManager.js line 79.
 */
export function sanitiseDuration(raw: string): string {
  return raw.replace(/[^0-9.-]/g, "");
}

/**
 * Normalise a duration value according to the specification:
 *
 * 1. If empty/falsy → `null` (means "until close").
 * 2. If negative (e.g. -1) → `null` (means "until close").
 * 3. If > 0 and <= 10 → multiply by 60 (hours → minutes).
 * 4. If > 10 → treat as already in minutes.
 * 5. Floor the result to get whole minutes.
 *
 * @param value - The raw duration value (string or number) from URL params.
 * @returns The normalised duration in minutes, or `null` for "until close".
 */
export function normaliseDuration(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numeric = typeof value === "string" ? Number(value) : value;

  if (Number.isNaN(numeric) || numeric <= 0) {
    return null;
  }

  if (numeric <= 10) {
    return Math.floor(numeric * 60);
  }

  return Math.floor(numeric);
}

/**
 * Parse and normalise a raw duration from a URL query parameter.
 *
 * Combines sanitisation and normalisation in one step.
 * If `time` is not provided, duration is forced to `null`
 * (duration is meaningless without a time).
 *
 * @param rawDuration - The raw duration string from the URL.
 * @param time - The time value from the URL (if any).
 * @returns The normalised duration in minutes, or `null`.
 */
export function parseDurationFromUrl(
  rawDuration: string | null | undefined,
  time: string | null | undefined,
): number | null {
  if (!time) {
    return null;
  }

  if (!rawDuration) {
    return null;
  }

  const sanitised = sanitiseDuration(rawDuration);
  return normaliseDuration(sanitised);
}
